import { LayoutDashboard, Pin, PinOff, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { WorkspaceTabRecord } from "@/contexts/workspaceState";

type WorkspaceTabsBarProps = {
  tabs: WorkspaceTabRecord[];
  activeTabId: string | null;
  onActivate: (id: string) => void;
  onClose: (id: string) => void;
  onPin: (id: string) => void;
  onUnpin: (id: string) => void;
  onCloseAll: () => void;
};

function getOrderedTabs(tabs: WorkspaceTabRecord[]) {
  return [...tabs].sort((left, right) => Number(right.pinned) - Number(left.pinned));
}

export function WorkspaceTabsBar({
  tabs,
  activeTabId,
  onActivate,
  onClose,
  onPin,
  onUnpin,
  onCloseAll,
}: WorkspaceTabsBarProps) {
  const orderedTabs = getOrderedTabs(tabs);

  return (
    <>
      <div className="flex-1 flex items-end h-full px-2 gap-1 overflow-x-auto hide-scrollbar pt-2">
        {orderedTabs.map((tab) => {
          const isActive = tab.id === activeTabId;
          const TabIcon = tab.icon ?? LayoutDashboard;

          return (
            <div
              key={tab.id}
              onClick={() => onActivate(tab.id)}
              className={cn(
                "group relative flex items-center gap-2 h-[34px] px-3 min-w-[140px] max-w-[240px] rounded-t-lg border border-b-0 cursor-pointer transition-colors text-[12px] select-none",
                isActive
                  ? "bg-white border-slate-200 text-slate-800 font-semibold z-10 before:absolute before:-bottom-[1px] before:left-0 before:right-0 before:h-[1px] before:bg-white"
                  : "bg-slate-100/50 border-transparent text-slate-500 hover:bg-slate-100",
              )}
            >
              <button
                type="button"
                aria-label={tab.pinned ? `Desfixar ${tab.label}` : `Fixar ${tab.label}`}
                title={tab.pinned ? "Desfixar" : "Fixar"}
                onClick={(event) => {
                  event.stopPropagation();
                  if (tab.pinned) {
                    onUnpin(tab.id);
                    return;
                  }

                  onPin(tab.id);
                }}
                className={cn(
                  "w-5 h-5 flex items-center justify-center rounded-sm shrink-0 transition-colors",
                  isActive
                    ? "text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                    : "text-slate-400 hover:text-slate-600 hover:bg-slate-200",
                )}
              >
                {tab.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
              </button>

              <button
                type="button"
                aria-label={`Abrir aba ${tab.label}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onActivate(tab.id);
                }}
                className="flex items-center gap-2 min-w-0 flex-1 text-left"
              >
                <TabIcon className={cn("w-3.5 h-3.5 shrink-0", isActive ? "text-primary" : "text-slate-400")} />
                <span className="truncate flex-1">{tab.label}</span>
              </button>

              <button
                type="button"
                aria-label={`Fechar aba ${tab.label}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onClose(tab.id);
                }}
                className={cn(
                  "w-4 h-4 flex items-center justify-center rounded-sm shrink-0 transition-opacity",
                  isActive
                    ? "hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                    : "opacity-0 group-hover:opacity-100 hover:bg-slate-200 text-slate-400",
                )}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          );
        })}
      </div>

      <div className="flex items-center h-full px-2 pb-[2px] shrink-0">
        <button type="button" className="tb-btn !h-8 !text-[11px]" onClick={onCloseAll}>
          Fechar todas
        </button>
      </div>
    </>
  );
}

export default WorkspaceTabsBar;
